import { useState } from 'react';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';

import {
  Box,
  Button,
  ButtonGroup,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
} from '@mui/material';

import { useBookkeeping, useEditPaymentMethodStore, usePaymentMethod } from '../../stores';
import BookkeepingAccount from '../../model/BookkeepingAccount';
import {
  DefaultNamesForPaymentMethodConfigTypes,
  DefaultShortNamesForPaymentMethodConfigTypes,
  PaymentMethodConfigTypes,
} from '../../model/PaymentMethodConfig';

interface TidypaySettingsProps {
  onDeletePaymethod: () => void;
  onClose: () => void;
}

const TidypaySettings = observer(({ onDeletePaymethod, onClose }: TidypaySettingsProps) => {
  const { accountList } = useBookkeeping();

  const { addPayMethod, updatePayMethod, setMessage } = usePaymentMethod();
  const editPaymentMethodStore = useEditPaymentMethodStore();

  const { t } = useTranslation();

  const [name, setName] = useState<string>(
    editPaymentMethodStore?.paymentMethod?.name ??
      DefaultNamesForPaymentMethodConfigTypes[PaymentMethodConfigTypes.TIDYPAY],
  );
  const [shortName, setShortName] = useState<string>(
    editPaymentMethodStore?.paymentMethod?.shortName ??
      DefaultShortNamesForPaymentMethodConfigTypes[PaymentMethodConfigTypes.TIDYPAY],
  );
  const [bookkeepingAccount, setBookkeepingAccount] = useState<string | undefined>(
    editPaymentMethodStore?.paymentMethod?.bookkeepingAccount ?? undefined,
  );
  const [active, setActive] = useState<boolean>(
    editPaymentMethodStore?.paymentMethod?.active ?? true,
  );

  const handleAddPaymethod = async () => {
    await addPayMethod({
      name,
      shortName,
      bookkeepingAccount,
      active,
      type: PaymentMethodConfigTypes.TIDYPAY,
    })
      .then(() => {
        setMessage(t('paymethodScreen.addPaymethodSuccess'), 'success');
      })
      .catch((error) => {
        setMessage(error.message, 'error');
      })
      .finally(() => {
        onClose();
      });
  };

  const handleUpdatePaymethod = async () => {
    if (!editPaymentMethodStore?.paymentMethod?.id) return;

    await updatePayMethod({
      id: editPaymentMethodStore.paymentMethod.id,
      name,
      shortName,
      bookkeepingAccount,
      active,
      type: PaymentMethodConfigTypes.TIDYPAY,
    })
      .then(() => {
        setMessage(t('paymethodScreen.editPaymethodSuccess'), 'success');
      })
      .catch((error) => {
        setMessage(error.message, 'error');
      })
      .finally(() => {
        onClose();
      });
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Grid container spacing={2} sx={{ marginTop: 0, marginBottom: 0 }}>
        <Grid item xs={12}>
          <TextField
            id="name"
            label={t('paymethodScreen.name')}
            variant="outlined"
            value={name || ''}
            onChange={(e) => setName(e.target.value)}
            fullWidth
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            id="shortName"
            label={t('paymethodScreen.shortName')}
            variant="outlined"
            value={shortName || ''}
            onChange={(e) => setShortName(e.target.value)}
            fullWidth
          />
        </Grid>
        {accountList.length !== 0 && (
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel id="account-id-label">
                {t('paymethodScreen.bookkeepingAccount')}
              </InputLabel>
              <Select
                labelId="account-id-label"
                id="account-id-select"
                value={bookkeepingAccount || ''}
                defaultValue=""
                label={t('paymethodScreen.bookkeepingAccount')}
                onChange={(e) => setBookkeepingAccount(e.target.value)}
              >
                {accountList.map((account: BookkeepingAccount) => (
                  <MenuItem key={account.id} value={account.id}>
                    {`${account.number} - ${account.name}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        )}
        <Grid item xs={12}>
          <FormControl>
            <FormGroup>
              <FormControlLabel
                control={
                  <Switch
                    checked={active || false}
                    onChange={(e) => setActive(e.target.checked)}
                    color="info"
                    name="active"
                  />
                }
                label={active ? t('paymethodScreen.active') : t('paymethodScreen.inactive')}
              />
            </FormGroup>
          </FormControl>
        </Grid>
      </Grid>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginTop: 'auto',
          paddingBottom: 2,
        }}
      >
        <ButtonGroup sx={{ display: 'flex', gap: 2 }}>
          {!editPaymentMethodStore.isNewPaymethod && (
            <Button variant="contained" color="error" onClick={onDeletePaymethod}>
              {t('paymethodScreen.removePaymethod')}
            </Button>
          )}
          <Button
            onClick={() =>
              editPaymentMethodStore.isNewPaymethod ? handleAddPaymethod() : handleUpdatePaymethod()
            }
            variant="contained"
          >
            {editPaymentMethodStore.isNewPaymethod
              ? t('paymethodScreen.add')
              : t('paymethodScreen.save')}
          </Button>
        </ButtonGroup>
      </Box>
    </Box>
  );
});

export default TidypaySettings;
